import React, { useRef, useEffect, useState, useCallback, useMemo, memo } from 'react';
import { Send, ChevronDown, Square } from 'lucide-react';
import Message from '../Message';

// Constants
const SCROLL_NEAR_BOTTOM_THRESHOLD = 100; // pixels
const USER_MESSAGE_WARNING_THRESHOLD = 10;
const SCROLL_DELAY = 300; // ms - delay before scrolling to target message
const HIGHLIGHT_DURATION = 1500; // ms - duration of highlight effect
const VISIBILITY_SCROLL_DELAY = 150; // ms - delay when panel becomes visible
const INTERSECTION_THRESHOLD = 0.1; // 10% visible

// Isolated input component — typing only re-renders this, not the message list
const ChatInput = memo(({ onSend, isChatLoading, onStopChat }) => {
  const [input, setInput] = useState('');

  const handleSendAndClear = useCallback(() => {
    if (!input.trim() || isChatLoading) return;
    onSend(input);
    setInput('');
  }, [input, onSend, isChatLoading]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendAndClear();
    }
  }, [handleSendAndClear]);

  return (
    <div className="p-8 pt-4 pb-2 w-full shrink-0 bg-gray-50/20 dark:bg-gray-900/10">
      <div className="mx-auto max-w-2xl relative bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-xl focus-within:ring-2 focus-within:ring-emerald-500 focus-within:border-emerald-500 transition-all duration-300">
        <textarea
          className="w-full p-4 pr-16 bg-transparent border-0 ring-0 focus:ring-0 focus:outline-none focus-visible:outline-none focus-visible:ring-0 text-sm resize-none min-h-[56px] max-h-32 text-gray-900 dark:text-white rounded-2xl"
          placeholder={isChatLoading ? "Chat is in progress... (you can type but can't send)" : "Ask anything..."}
          rows={1}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <div className="absolute right-2 bottom-2 flex items-center gap-1">
          {isChatLoading ? (
            <button
              onClick={onStopChat}
              className="bg-red-600 text-white p-2 rounded-xl hover:bg-red-700 transition-all shadow-md"
              title="Stop chat"
            >
              <Square size={20} />
            </button>
          ) : (
            <button
              onClick={handleSendAndClear}
              disabled={!input.trim()}
              className="bg-emerald-600 text-white p-2 rounded-xl hover:bg-emerald-700 disabled:opacity-50 transition-all shadow-md"
            >
              <Send size={20} />
            </button>
          )}
        </div>
      </div>
      <div className="px-8 mt-3 pb-1">
        <p className="text-center text-xs text-gray-400 dark:text-gray-500 max-w-2xl mx-auto">
          Manteo AI can make mistakes. Check important info.
        </p>
      </div>
    </div>
  );
});

const ChatPanel = ({ messages, chatHeader = null, onSend, targetMessageId, onScrollComplete, isChatLoading = false, isInitializingChat = false, onStopChat, onNewChat, onAddLayer, onViewMapLayer }) => {
  const [lastMessageCount, setLastMessageCount] = useState(messages.length);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const scrollRef = useRef(null);
  const wasVisibleRef = useRef(false);

  // Count user messages
  const userMessageCount = useMemo(() => messages.filter(m => m.role === 'user').length, [messages]);
  const showWarning = userMessageCount >= USER_MESSAGE_WARNING_THRESHOLD;

  // Helper function to scroll to bottom (DRY)
  const scrollToBottom = useCallback((smooth = false) => {
    if (!scrollRef.current) return;
    if (smooth) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    } else {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    setShowScrollButton(false);
  }, []);

  useEffect(() => {
    if (targetMessageId) {
      // Small delay to ensure DOM has updated after messages state change
      const timer = setTimeout(() => {
        const element = document.getElementById(`message-${targetMessageId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });

          // Temporary highlight effect using CSS class
          element.classList.add('message-highlight');
          setTimeout(() => {
            element.classList.remove('message-highlight');
            onScrollComplete?.();
          }, HIGHLIGHT_DURATION);
        }
      }, SCROLL_DELAY);
      return () => clearTimeout(timer);
    } else if (messages.length > lastMessageCount) {
      // Only auto-scroll to bottom if messages count increased (new message)
      scrollToBottom(false);
    }
    setLastMessageCount(messages.length);
  }, [messages, targetMessageId, onScrollComplete, lastMessageCount, scrollToBottom]);

  useEffect(() => {
    const handleScroll = () => {
      if (!scrollRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < SCROLL_NEAR_BOTTOM_THRESHOLD;
      setShowScrollButton(!isNearBottom);
    };

    // Initial check
    if (scrollRef.current) {
      handleScroll();
    }

    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll);
      return () => scrollElement.removeEventListener('scroll', handleScroll);
    }
  }, [messages]);

  // Scroll to bottom when panel becomes visible (re-opened)
  useEffect(() => {
    const currentScrollRef = scrollRef.current;
    if (!currentScrollRef) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const isVisible = entry.isIntersecting;
          // When panel becomes visible (was hidden, now visible) and has messages, scroll to bottom
          if (isVisible && !wasVisibleRef.current && messages.length > 0 && !targetMessageId && !isInitializingChat) {
            setTimeout(() => scrollToBottom(false), VISIBILITY_SCROLL_DELAY);
          }
          wasVisibleRef.current = isVisible;
        });
      },
      { threshold: INTERSECTION_THRESHOLD }
    );

    observer.observe(currentScrollRef);

    return () => {
      if (currentScrollRef) {
        observer.unobserve(currentScrollRef);
      }
    };
  }, [messages.length, targetMessageId, isInitializingChat, scrollToBottom]);

  const handleScrollToBottom = useCallback(() => {
    scrollToBottom(true); // Use smooth scroll for button click
  }, [scrollToBottom]);

  const handleSend = useCallback(
    (text) => {
      if (!text || !text.trim() || isChatLoading) return;
      onSend(text);
    },
    [onSend, isChatLoading]
  );

  const visibleMessages = useMemo(() => {
    return messages.filter(m => {
      // Filter out hidden messages
      if (m.type?.startsWith('hidden_') || m.type === 'map_spec') {
        return false;
      }
      // Filter out artifact messages (empty content with artifact data in extra)
      // Also filter legacy messages with "Artifact:" prefix
      if ((!m.content || m.content.trim() === '') && m.extra?.type && m.extra?.title) {
        return false;
      }
      if (m.content && m.content.trim().startsWith('Artifact:')) {
        return false;
      }
      return true;
    });
  }, [messages]);

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-950 overflow-hidden border-r border-gray-100 dark:border-gray-800 relative">
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-8 space-y-4">
        <div className="w-full max-w-2xl mx-auto">
          {chatHeader}
          {isInitializingChat && messages.length === 0 ? (
            <div className="flex items-center justify-center h-full min-h-[200px]">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading chat...</p>
              </div>
            </div>
          ) : (
            visibleMessages.map((m, i) => (
              <Message key={m.id || i} message={m} onSuggestionClick={handleSend} onAddLayer={onAddLayer} onViewMapLayer={onViewMapLayer} />
            ))
          )}
        </div>
      </div>
      {showScrollButton && (
        <button
          onClick={handleScrollToBottom}
          className="absolute bottom-24 left-1/2 transform -translate-x-1/2 p-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-full shadow-lg hover:bg-white/90 dark:hover:bg-gray-800/90 transition-all z-20"
          aria-label="Scroll to bottom"
        >
          <ChevronDown size={20} className="text-gray-600 dark:text-gray-300" />
        </button>
      )}
      {showWarning && (
        <div className="px-8 pb-2 w-full shrink-0">
          <div className="mx-auto max-w-2xl">
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-800/30 rounded-lg">
              <span className="text-xs text-amber-700 dark:text-amber-400 flex-1">
                This chat has {userMessageCount} user messages. Consider starting a new chat for better performance.
              </span>
              {onNewChat && (
                <button
                  onClick={onNewChat}
                  className="text-xs font-medium text-amber-700 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 underline transition-colors shrink-0"
                >
                  New Chat
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <ChatInput onSend={handleSend} isChatLoading={isChatLoading} onStopChat={onStopChat} />
    </div>
  );
};

export default ChatPanel;
